// Die Unterlagen für die Montage (E3, S4): jede freigegebene Baustelle, der
// nächste Montagetermin zuerst – so ordnet der Server (GET
// /api/crm/unterlagen): kommende Termine, dann ohne Termin, zuletzt
// vergangene. Die Seite gibt den drei Abschnitten nur eine Überschrift. Ein
// Klick auf eine Zeile öffnet die Unterlage zum Drucken (#/unterlage/<id>).
//
// Wer sie sieht: Montage und Inbetriebnahme (ihre Startseite), Büro und
// Leitung (Recht „unterlagen“, Kern rechte.ts).
//
// Seit E3c (S4) ist die Seite für Leitung und Büro die Kiste Montage: oben,
// welche Partie an welchem Tag wo ist (../kisten/KistenSeiten.tsx,
// MontagePlan – Recht „einsatz“), darunter die Unterlagen wie bisher.

import { darf, type UnterlagenAntwort, type UnterlagenZeile } from "@happy-energy/kern";
import { Ikon, Spaeter, Tipp } from "@happy-energy/oberflaeche";
import { adresseZeigen, heuteInWien, kuerzel, zeitpunktZeigen } from "../anzeige.ts";
import { Laedt, LadeFehler, useLaden } from "../laden.tsx";
import { MontagePlan } from "../kisten/KistenSeiten.tsx";
import { geheZu, Seite, SucheSpaeter } from "../rahmen.tsx";
import { useCrm } from "../zusammenhang.tsx";
import { montageterminText, unterlagenGruppen } from "./unterlage.ts";
import "./unterlage.css";

/**
 * Eine Baustelle. Leitung und Büro kommen mit einem Klick in die Akte der
 * Anlage, „Unterlage“ öffnet die Unterlage (Gernots Augentest E3c,
 * 3.11.2026: „komme ich nur zur Unterlage … nicht in den Akt selbst“); die
 * Montage sieht keine Akte – für sie führt alles zur Unterlage.
 */
function Zeile({ z, heute, zurAkte }: { z: UnterlagenZeile; heute: string; zurAkte: boolean }) {
  const termin = z.montagetermin;
  const istHeute = termin.am === heute;
  const unterlage = `/unterlage/${z.aufnahmeId}`;
  const ziel = zurAkte ? `/anlage/${z.anlageId}` : unterlage;
  return (
    <tr className="klickbar" onClick={() => geheZu(ziel)}>
      <td className="nicht-umbrechen">
        <span className={termin.am || termin.text ? (istHeute ? "termin heute" : "termin") : "termin leise"}>
          {istHeute && <b>heute · </b>}
          {montageterminText(termin)}
        </span>
      </td>
      <td>
        <div className="tabelle-kunde">
          <span className="avatar avatar-lime">{kuerzel(z.kundeName)}</span>
          <div>
            {/* Der Name ist der Link – mit der Tastatur erreichbar; die ganze Zeile reagiert auf die Maus. */}
            <a href={`#${ziel}`} onClick={(e) => e.stopPropagation()}>
              {z.kundeName}
            </a>
            <span className="tabelle-unter">{adresseZeigen(z)}</span>
          </div>
        </div>
      </td>
      <td className="nicht-umbrechen">{zeitpunktZeigen(z.freigegebenAm)}</td>
      <td className="nicht-umbrechen unterlagen-oeffnen">
        <a href={`#${unterlage}`} onClick={(e) => e.stopPropagation()}>
          <Ikon name="datei" groesse="k" /> Unterlage
        </a>
      </td>
    </tr>
  );
}

export function UnterlagenSeite() {
  const { ich } = useCrm();
  const { daten, fehler, neu } = useLaden<UnterlagenAntwort>("/api/crm/unterlagen");
  const heute = heuteInWien();
  const gruppen = unterlagenGruppen(daten?.unterlagen ?? [], heute);
  // Leitung und Büro sehen hier die Kiste Montage – welche Partie wann wo; die Montage selbst ihre Unterlagen.
  const mitPlan = darf(ich.nutzer.rolle, "einsatz");

  return (
    <Seite
      titel={mitPlan ? "Montage" : "Unterlagen"}
      unter={mitPlan ? "Welche Partie wann wo – und die Unterlage jeder Baustelle" : "Für die Montage – der nächste Montagetermin zuerst"}
      knoepfe={<SucheSpaeter />}
    >
      {mitPlan && <MontagePlan />}
      {fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
      {!daten && fehler === undefined && <Laedt text="Die Unterlagen werden geladen …" />}
      {daten && daten.unterlagen.length === 0 && (
        <Tipp>Noch keine Unterlage – sie entsteht, sobald das Büro eine Besichtigung freigibt. Dann steht sie hier, zum Drucken oder als PDF.</Tipp>
      )}
      {gruppen.map((g) => (
        <section key={g.titel} className="unterlagen-gruppe" aria-label={g.titel}>
          <div className="crm-abschnitt">
            <h2>{g.titel}</h2>
            <span className="klein leise">{g.zeilen.length === 1 ? "1 Baustelle" : `${g.zeilen.length} Baustellen`}</span>
          </div>
          <div className="tabelle-wrap">
            <table className="tabelle unterlagen-tabelle">
              <thead>
                <tr>
                  <th scope="col">Montagetermin</th>
                  <th scope="col">Kunde und Adresse</th>
                  <th scope="col">Freigegeben</th>
                  <th scope="col">
                    <span className="unterlage-vorleser">Öffnen</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {g.zeilen.map((z) => (
                  <Zeile key={z.aufnahmeId} z={z} heute={heute} zurAkte={darf(ich.nutzer.rolle, "kunden")} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
      <Spaeter titel="Baustellen der Woche je Partie" wann="Kiste Montage">
        Jede Partie sieht ihre Baustellen der Woche – mit der Unterlage Schritt für Schritt in der eigenen App und „fertig“ an die Inbetriebnahme.
      </Spaeter>
    </Seite>
  );
}
